import React, { useState, useMemo, useCallback, useRef, useLayoutEffect, FC, useEffect } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { faHomeLgAlt, faLink, faUserFriends, faBuilding, faUserCrown, faNewspaper, faBars } from '@fortawesome/pro-regular-svg-icons';
import { faHomeLgAlt as solidfaHomeLgAlt, faUserFriends as solidfaUserFriends, faBuilding as solidfaBuilding, faUserCrown as solidfaUserCrown, faNewspaper as solidfaNewspaper, faStamp as solidfaStamp, faCalendar as solidfaCalendar } from '@fortawesome/pro-solid-svg-icons';
import { useLocation } from 'react-router-dom';

import { EMenuItem, ILevel1MenuItem } from '../../../entities/IMenu';
import { EPlatformPermissions } from '../../../entities/IPermissions';
import { IState } from '../../../reducers';
import { IOrganizationReducer } from '../../../reducers/organizationReducer';
import { IContextReducer } from '../../../reducers/contextReducer';
import { IProfileReducer } from '../../../reducers/profileReducer';
import { setOrganizationsName } from '../../../actions/organizationActions';
import { isPlatformAdmin } from '../../../tools/permissions';
import { selectApprovalEnabled, selectNewsEnabled } from '../../../tools/platformConfiguration';
import { typed } from '../../../tools/generalTools';
import { useResize } from '../../../tools/hooks';
import { MenuItem } from './MenuItem';
import { OrganizationsScroller } from '../../Layout/NavMenu/Components/OrganizationsScroller';
import { TextComponent } from '../../Common/Inputs/TextComponent';
import { useEscKey } from '../../../tools/keyboardTools';
import { useUsefulLinksPopup } from '../../../tools/usefulLinksHooks';
import {
    isUsefulLinksPopupQueryOpen,
    shouldOpenUsefulLinksPopupFromQuery,
    withUsefulLinksPopupQuery
} from '../../../tools/usefulLinksQuery';
import { useIntlMessage } from '../../Common/IntlMessage';
import { IMenuReducer } from '../../../reducers/menuReducer';
import { useDatatableContext } from '../../Common/ColoredDatagrid/DatatableContext';
import { EDatatableIds } from '../../Common/ColoredDatagrid/DataTableTools';

const Wrapper = styled.div`
    height: 100%;
    overflow: hidden;
    > div {
        display: flex;
        flex-direction: column;
    }
`;

const Filter = styled.div`
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 1rem 0 1rem;
    > div {
        flex: 1;
        width: 100%;
    }
`;

interface IProps {
    fullMenu?: boolean;
    isDesktopMenuVisible?: boolean;
    onMenuItemClick();
    onFilterClick?();
}

export const SidebarMenu: FC<React.PropsWithChildren<IProps>> = ({ fullMenu, isDesktopMenuVisible, onMenuItemClick, onFilterClick }) => {
    const { intlFormatMessage } = useIntlMessage();
    const dispatch = useDispatch();
    const datatableContext = useDatatableContext();
    const { search } = useLocation();
    const showUsefulLinksPopup = useUsefulLinksPopup();
    const usefulLinksQueryOpen = isUsefulLinksPopupQueryOpen(search);
    const openedUsefulLinksPopupRef = useRef(false);
    const wrapperRef = useRef<HTMLDivElement>();
    const listRef = useRef<HTMLDivElement>();
    const searchInputRef = useRef<HTMLInputElement>();
    const [searchInputValue, setSearchInputValue] = useState<string>('');
    const [visibleMenuItemsCount, setVisibleMenuItemsCount] = useState<number>(0);
    const { innerHeight, innerWidth } = useResize();
    const { currentUserProfile, contextOrganizations, currentUserPermissions, menuItem, newsEnabled, approvalEnabled } = useSelector<IState, IOrganizationReducer & IMenuReducer & IContextReducer & IProfileReducer & { newsEnabled: boolean; approvalEnabled: boolean }>(state => ({
        ...state.organization,
        ...state.context,
        ...state.profile,
        ...state.menu,
        newsEnabled: selectNewsEnabled(state),
        approvalEnabled: selectApprovalEnabled(state)
    }));

    const showUsefulLinks = useCallback(() => {
        openedUsefulLinksPopupRef.current = true;
        datatableContext.clearContext(EDatatableIds.exploreDataTable);
        onMenuItemClick();
        showUsefulLinksPopup();
    }, [datatableContext, onMenuItemClick, showUsefulLinksPopup]);

    useEffect(() => {
        if (shouldOpenUsefulLinksPopupFromQuery(usefulLinksQueryOpen, openedUsefulLinksPopupRef.current)) {
            showUsefulLinks();
            return;
        }

        if (!usefulLinksQueryOpen) {
            openedUsefulLinksPopupRef.current = false;
        }
    }, [showUsefulLinks, usefulLinksQueryOpen]);

    const escKeyHandler = useCallback(() => {
        if (!fullMenu) return;

        searchInputValue
            ? setSearchInputValue('')
            : onMenuItemClick();
    }, [searchInputValue, fullMenu, onMenuItemClick]);

    useEscKey(escKeyHandler);

    const menuItems = useMemo<ILevel1MenuItem[]>(() => [typed<ILevel1MenuItem>(contextOrganizations?.ids?.length > 1 && {
        name: intlFormatMessage({id: 'topnav.home'}),
        link: '/',
        faIcon: menuItem === EMenuItem.home ? solidfaHomeLgAlt : faHomeLgAlt,
        className: menuItem === EMenuItem.home ? 'last-item active' : 'last-item'
    })].concat(approvalEnabled && {
        name: intlFormatMessage({id: 'topnav.approvals'}),
        link: '/approvals',
        faIcon: solidfaStamp,
        className: menuItem === EMenuItem.userApprovals ? 'active' : ''
    }).concat({
        name: intlFormatMessage({id: 'topnav.dates'}),
        link: '/dates',
        faIcon: solidfaCalendar,
        className: menuItem === EMenuItem.userKeyDates ? 'last-item active' : 'last-item'
    }).concat((newsEnabled && currentUserPermissions?.[EPlatformPermissions.ArticlesView]) && {
        name: intlFormatMessage({id: 'topnav.news'}),
        link: '/news',
        faIcon: menuItem === EMenuItem.publications ? solidfaNewspaper : faNewspaper,
        className: menuItem === EMenuItem.publications ? 'active' : ''
    }).concat(currentUserPermissions?.[EPlatformPermissions.UsersSearch] && {
        name: intlFormatMessage({id: 'topnav.main.people'}),
        link: '/directory',
        faIcon: menuItem === EMenuItem.people ? solidfaUserFriends : faUserFriends,
        className: menuItem === EMenuItem.people ? 'active' : ''
    }).concat(currentUserPermissions?.[EPlatformPermissions.PlatformEntitiesView] && {
        name: intlFormatMessage({id: 'topnav.main.entities'}),
        link: '/companies',
        faIcon: menuItem === EMenuItem.entities ? solidfaBuilding : faBuilding,
        className: menuItem === EMenuItem.entities ? 'active' : ''
    }).concat(currentUserPermissions?.[EPlatformPermissions.UsefulLinksView] && {
        name: intlFormatMessage({id: 'orgshome.title.usefullinks'}),
        link: withUsefulLinksPopupQuery(search),
        faIcon: faLink,
        className: isPlatformAdmin(currentUserPermissions) ? '' : 'last-item',
        onClick: showUsefulLinks
    }).concat(isPlatformAdmin(currentUserPermissions) && {
        name: intlFormatMessage({id: 'topnav.adminConsole'}),
        link: '/admin-console',
        faIcon: menuItem === EMenuItem.administration ? solidfaUserCrown : faUserCrown,
        className: menuItem === EMenuItem.administration ? 'last-item active' : 'last-item'
    }).concat(contextOrganizations?.ids?.map(organizationId => {
        const organization = contextOrganizations.byId[organizationId];
        return {
            name: organization?.details?.name,
            link: `/orgs/${organization.details.urlName}/explore`,
            imagePath: organization.details.logo
        };
    })).filter(value => !!value), [menuItem, intlFormatMessage, contextOrganizations, search, showUsefulLinks, currentUserProfile, newsEnabled, approvalEnabled, currentUserPermissions]);

    const filteredMenuItems = useMemo(() => menuItems
        .filter(menuItemElem => menuItemElem
            ?.name
            ?.toLowerCase()
            ?.includes(searchInputValue.trim().toLowerCase())
        ), [menuItems, searchInputValue, intlFormatMessage, contextOrganizations]);

    useLayoutEffect(() => {
        if (!fullMenu) {
            const itemsCount = Array
                .from(listRef.current?.children || [])
                .reduce((sum, menuItemElem) => {
                    const item = menuItemElem as unknown as HTMLDivElement;
                    return item.offsetTop + item.offsetHeight < wrapperRef.current.offsetHeight ? ++sum : sum;
                }, 0);

            setVisibleMenuItemsCount(itemsCount);
        }
    }, [innerHeight, innerWidth, filteredMenuItems, fullMenu]);

    const onMenuClick = useCallback((organizationUrlName: string) => {
        datatableContext.clearContext(EDatatableIds.exploreDataTable);
        onMenuItemClick();
        organizationUrlName && dispatch(setOrganizationsName(organizationUrlName));
    }, [onMenuItemClick]);

    const clearSearchResults = useCallback(() => setSearchInputValue(''), []);

    useLayoutEffect(() => {
        isDesktopMenuVisible && searchInputRef.current?.focus();
    }, [isDesktopMenuVisible]);

    const onChangeSearchValue = (value: string) => {
        if (!isDesktopMenuVisible) onFilterClick?.();
        setSearchInputValue(value);
    };

    return (
        <Wrapper ref={wrapperRef}>
            <OrganizationsScroller ref={listRef} scrollerDisabled={!fullMenu}>
                {contextOrganizations?.ids?.length > 1 && (
                    <>
                        {fullMenu ? (
                            <Filter>
                                <div>
                                    <TextComponent
                                        ref={searchInputRef}
                                        value={searchInputValue}
                                        onChange={onChangeSearchValue}
                                        placeholder={intlFormatMessage({ id: 'global.filter' })}
                                        onClearClick={clearSearchResults}
                                    />
                                </div>
                            </Filter>
                        ) : (
                                <MenuItem
                                    onClick={onFilterClick}
                                    faIcon={faBars}
                                />
                            )}
                    </>
                )}
                {currentUserProfile?.id && filteredMenuItems.map((menuItemElem, index) => (
                    <MenuItem
                        key={index}
                        name={menuItemElem.name}
                        fullMenu={fullMenu}
                        link={menuItemElem.link}
                        orgUrlName={menuItemElem.orgUrlName}
                        onClick={menuItemElem.onClick || onMenuClick}
                        imagePath={menuItemElem.imagePath}
                        faIcon={menuItemElem.faIcon}
                        className={menuItemElem.className}
                        isHidden={!fullMenu && (index > (visibleMenuItemsCount - 1))}
                        isDesktopMenuVisible={isDesktopMenuVisible}
                    />
                ))}
            </OrganizationsScroller>
        </Wrapper>
    );
};
